import { DEV_AUTH_BYPASS, getSupabase } from "./auth";
import type { ChatMessage, ChatToolBlock } from "./types";

/**
 * SSE client for POST /admin/api/chat/stream.
 *
 * fetch()'s ReadableStream gives us a real SSE-over-POST channel without
 * needing EventSource (which only supports GET). We parse the stream by
 * splitting on \n\n event records.
 *
 * The handler returns an updater that mutates the in-progress assistant
 * message. The caller owns the message list; we don't store anything here.
 */

export interface StreamHandlers {
  onToken: (delta: string) => void;
  onToolUse: (block: ChatToolBlock) => void;
  onToolResult: (id: string, output: unknown, isError: boolean) => void;
  onConfirmRequest: (prompt: string, token?: string) => void;
  onTurnEnd: (info: { reason: string; cost_cents: number }) => void;
  onError: (msg: string) => void;
}

export interface StreamRequest {
  session_id: string;
  messages: ChatMessage[];
  tools_allowed?: string[];
  signal?: AbortSignal;
}

export async function streamChat(req: StreamRequest, h: StreamHandlers): Promise<void> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "text/event-stream",
  };
  if (DEV_AUTH_BYPASS) {
    headers["x-dev-auth"] = "1";
    headers["x-dev-email"] = "dev@ultraviolet.club";
  } else {
    const supabase = getSupabase();
    if (supabase) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) headers["authorization"] = `Bearer ${session.access_token}`;
    }
  }

  let res: Response;
  try {
    res = await fetch("/admin/api/chat/stream", {
      method: "POST",
      headers,
      body: JSON.stringify({
        session_id: req.session_id,
        messages: req.messages.map((m) => ({ role: m.role, content: m.content })),
        tools_allowed: req.tools_allowed,
      }),
      credentials: "include",
      signal: req.signal,
    });
  } catch (e) {
    h.onError((e as Error).message ?? "Network error");
    return;
  }
  if (!res.ok || !res.body) {
    h.onError(`HTTP ${res.status}`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  // Track tool blocks so tool_result updates the right one.
  const tools = new Map<string, ChatToolBlock>();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseSseRecord(raw);
      if (!ev) continue;
      try {
        switch (ev.event) {
          case "token":
            h.onToken(String(ev.data?.delta ?? ""));
            break;
          case "tool_use": {
            const block: ChatToolBlock = {
              id: String(ev.data?.id ?? ""),
              name: String(ev.data?.name ?? ""),
              input: (ev.data?.input ?? {}) as Record<string, unknown>,
              state: "running",
            };
            tools.set(block.id, block);
            h.onToolUse(block);
            break;
          }
          case "tool_result": {
            const id = String(ev.data?.id ?? "");
            const isError = !!ev.data?.is_error;
            h.onToolResult(id, ev.data?.output, isError);
            const t = tools.get(id);
            if (t) t.state = isError ? "error" : "complete";
            break;
          }
          case "confirm_request":
            h.onConfirmRequest(
              String(ev.data?.prompt ?? "Should I proceed?"),
              ev.data?.token as string | undefined,
            );
            break;
          case "turn_end":
            h.onTurnEnd({
              reason: String(ev.data?.reason ?? "end_turn"),
              cost_cents: Number(ev.data?.cost_cents ?? 0),
            });
            break;
          case "error":
            h.onError(String(ev.data?.message ?? "stream error"));
            break;
        }
      } catch (e) {
        // Continue reading even if a single event throws.
        console.warn("[chat] event handler threw", e);
      }
    }
  }
}

interface ParsedSse {
  event: string;
  data: Record<string, unknown> | null;
}

function parseSseRecord(raw: string): ParsedSse | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (!line) continue;
    if (line.startsWith(":")) continue; // comment
    const c = line.indexOf(":");
    if (c === -1) continue;
    const k = line.slice(0, c).trim();
    const v = line.slice(c + 1).replace(/^\s/, "");
    if (k === "event") event = v;
    else if (k === "data") dataLines.push(v);
  }
  if (!dataLines.length) return null;
  const dataStr = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(dataStr) };
  } catch {
    return { event, data: null };
  }
}

/* ---------- session storage ---------- */

const STORAGE_KEY = "kitt-admin-chat-v1";

export interface ChatSessionState {
  session_id: string;
  messages: ChatMessage[];
}

export function loadChatSession(): ChatSessionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ChatSessionState;
      if (parsed.session_id && Array.isArray(parsed.messages)) return parsed;
    }
  } catch {
    /* fall through */
  }
  return { session_id: crypto.randomUUID(), messages: [] };
}

export function saveChatSession(s: ChatSessionState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* over quota — ignore */
  }
}

export function newChatSession(): ChatSessionState {
  const s: ChatSessionState = { session_id: crypto.randomUUID(), messages: [] };
  saveChatSession(s);
  return s;
}
